import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';

interface Named { id: string; name: string; }
interface Recipient { recipient_email: string; recipient_name: string | null; recipient_type: string; status: string; error_message: string | null; }
interface Email {
  id: string; subject: string; body_md: string; mailing_list_id: string | null; mailing_list_name?: string | null;
  status: 'draft' | 'sent'; recipient_count: number; sent_at: string | null; updated_at: string; recipients?: Recipient[];
}
type Form = { subject: string; body_md: string; mailing_list_id: string };

/**
 * <cadence-mailing-emails> — the Emails tab. Compose against a mailing list, save as a
 * draft, preview, then send. Sending snapshots the list's recipients and marks the email
 * sent (actual delivery is wired in Phase 4.6). Any email can be cloned as a new draft.
 */
@customElement('cadence-mailing-emails')
export class CadenceMailingEmails extends LitElement {
  @state() private emails: Email[] = [];
  @state() private lists: Named[] = [];
  @state() private loading = true;
  @state() private selectedId: string | null = null;
  @state() private detail: Email | null = null; // loaded full email (sent view / recipients)
  @state() private form: Form = { subject: '', body_md: '', mailing_list_id: '' };
  @state() private saving = false;
  @state() private sending = false;
  @state() private showPreview = false;
  @state() private showSendConfirm = false;
  @state() private sendingTest = false;
  @state() private previewExpanded = false;
  @state() private recipientInfo: { total: number; subscribed: number } | null = null;
  @state() private loadingRecipients = false;
  @state() private errorDetail: { email: string; message: string } | null = null;

  private readonly canManage = store.hasPermission('mailing.manage');
  private readonly canSend = store.hasPermission('mailing.send');

  static styles = css`
    :host { display: block; }
    .layout { display: grid; grid-template-columns: 280px 1fr; gap: var(--space-4); align-items: start; }
    @media (max-width: 860px) { .layout { grid-template-columns: 1fr; } }
    .list { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
    .eitem { text-align: left; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 10px 12px; cursor: pointer; font: inherit; color: var(--color-text-primary); }
    .eitem:hover { border-color: var(--color-primary); }
    .eitem[aria-selected='true'] { border-color: var(--color-primary); background: color-mix(in srgb, var(--color-primary) 8%, transparent); }
    .eitem .s { font-weight: 600; display: flex; align-items: center; gap: 8px; justify-content: space-between; }
    .eitem .m { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
    .builder { display: grid; gap: var(--space-4); }
    .field { margin-bottom: var(--space-3); }
    .field label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    .muted { color: var(--color-text-secondary); font-size: 0.85rem; }
    .actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
    .rm { cursor: pointer; color: var(--color-danger); background: none; border: none; font: inherit; }
    .rtable { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
    .rtable th, .rtable td { text-align: left; padding: 7px 10px; border-bottom: 1px solid var(--color-border); }
    .rtable th { color: var(--color-text-secondary); font-weight: 600; }
    .pill { font-size: 0.7rem; border-radius: 999px; padding: 1px 8px; border: 1px solid var(--color-border); color: var(--color-text-secondary); }
    .pill-fail { color: var(--color-danger); border-color: color-mix(in srgb, var(--color-danger) 45%, var(--color-border)); }
    .errbtn { background: none; border: none; cursor: pointer; color: var(--color-danger); padding: 0; margin-left: 6px; vertical-align: middle; display: inline-flex; }
    .errbtn svg { display: block; }
    .errtext { white-space: pre-wrap; word-break: break-word; margin: 0; padding: 10px 12px; font-size: 0.82rem; border-radius: var(--radius-sm); border: 1px solid color-mix(in srgb, var(--color-danger) 35%, var(--color-border)); background: color-mix(in srgb, var(--color-danger) 8%, transparent); }
    .sent-body { border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: var(--space-4); }
    /* Email-like preview frame */
    .mailframe { border: 1px solid var(--color-border); border-radius: var(--radius-md); overflow: hidden; }
    .mf-subject { font-weight: 700; font-size: 1.05rem; padding: 12px 16px 4px; }
    .mf-from { color: var(--color-text-secondary); font-size: 0.78rem; padding: 0 16px 12px; border-bottom: 1px solid var(--color-border); }
    .mf-body { padding: 16px; }
    .mf-hr { border: none; border-top: 1px solid var(--color-border); margin: 0 16px; }
    .mf-unsub { padding: 10px 16px 16px; margin: 0; font-size: 0.72rem; color: var(--color-text-secondary); }
    /* Send-confirmation overlay */
    .summary { margin: 0 0 16px; display: grid; gap: 8px; }
    .summary .srow { display: grid; grid-template-columns: 110px 1fr; gap: 10px; align-items: baseline; }
    .summary dt { color: var(--color-text-secondary); font-size: 0.82rem; }
    .summary dd { margin: 0; }
    .pv-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; font-size: 0.82rem; color: var(--color-text-secondary); }
    .linkbtn { background: none; border: none; cursor: pointer; font: inherit; font-size: 0.82rem; color: var(--color-primary); padding: 0; }
    .previewbox.collapsed { max-height: 220px; overflow: hidden; -webkit-mask-image: linear-gradient(180deg, #000 65%, transparent); mask-image: linear-gradient(180deg, #000 65%, transparent); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      this.emails = await api.get<Email[]>('/mailing/emails');
      this.lists = await api.get<Named[]>('/mailing/lists').catch(() => []);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private newEmail() {
    this.selectedId = null; this.detail = null;
    this.form = { subject: '', body_md: '', mailing_list_id: '' };
  }
  private async selectEmail(id: string) {
    this.selectedId = id;
    try {
      this.detail = await api.get<Email>(`/mailing/emails/${id}`);
      this.form = { subject: this.detail.subject, body_md: this.detail.body_md, mailing_list_id: this.detail.mailing_list_id ?? '' };
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async reload(selectId?: string) {
    this.emails = await api.get<Email[]>('/mailing/emails').catch(() => this.emails);
    if (selectId) await this.selectEmail(selectId);
  }

  /** Persist the working draft (create or update) and return its id. */
  private async ensureSaved(): Promise<string | null> {
    const body = { subject: this.form.subject.trim(), body_md: this.form.body_md, mailing_list_id: this.form.mailing_list_id || null };
    try {
      if (this.selectedId) { await api.put(`/mailing/emails/${this.selectedId}`, body); return this.selectedId; }
      const created = await api.post<Email>('/mailing/emails', body);
      this.selectedId = created.id;
      return created.id;
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('mailing.toast.draftSaveFailed'), 'error'); return null; }
  }

  private async saveDraft() {
    if (!this.form.subject.trim()) { showToast(t('mailing.toast.draftSubjectRequired'), 'error'); return; }
    this.saving = true;
    const id = await this.ensureSaved();
    this.saving = false;
    if (id) { await this.reload(id); showToast(t('mailing.toast.draftSaved'), 'success'); }
  }

  /** Open the styled send-confirmation overlay. Saves the draft first so both the test
   *  send and the real send operate on the latest content, then resolves recipient counts. */
  private async openSendDialog() {
    if (!this.form.subject.trim()) { showToast(t('mailing.toast.sendSubjectRequired'), 'error'); return; }
    if (!this.form.body_md.trim()) { showToast(t('mailing.toast.bodyEmpty'), 'error'); return; }
    if (!this.form.mailing_list_id) { showToast(t('mailing.toast.listRequired'), 'error'); return; }
    this.saving = true;
    const id = await this.ensureSaved();
    this.saving = false;
    if (!id) return;
    this.previewExpanded = false;
    this.showSendConfirm = true;
    this.loadRecipientInfo();
  }

  private async loadRecipientInfo() {
    this.recipientInfo = null;
    if (!this.form.mailing_list_id) return;
    this.loadingRecipients = true;
    try {
      const info = await api.get<{ total: number; subscribed: number }>(`/mailing/lists/${this.form.mailing_list_id}/preview`);
      this.recipientInfo = { total: info.total, subscribed: info.subscribed };
    } catch { this.recipientInfo = null; }
    finally { this.loadingRecipients = false; }
  }

  private async confirmSend() {
    const id = this.selectedId;
    if (!id) return;
    this.sending = true;
    try {
      await api.post(`/mailing/emails/${id}/send`, {});
      this.showSendConfirm = false;
      await this.reload(id);
      showToast(t('mailing.toast.emailSent'), 'success');
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('mailing.toast.sendFailed'), 'error'); }
    finally { this.sending = false; }
  }

  /** Send the current draft to the logged-in user's own email address. */
  private async sendTest() {
    if (!this.form.subject.trim()) { showToast(t('mailing.toast.testSubjectRequired'), 'error'); return; }
    if (!this.form.body_md.trim()) { showToast(t('mailing.toast.bodyEmpty'), 'error'); return; }
    this.sendingTest = true;
    try {
      const id = await this.ensureSaved();
      if (!id) return;
      const res = await api.post<{ to: string; sandbox: boolean }>(`/mailing/emails/${id}/test`, {});
      showToast(res.sandbox ? t('mailing.toast.testValidated', { email: res.to }) : t('mailing.toast.testSent', { email: res.to }), 'success');
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('mailing.toast.testFailed'), 'error'); }
    finally { this.sendingTest = false; }
  }

  private async clone(id: string) {
    try { const copy = await api.post<Email>(`/mailing/emails/${id}/clone`, {}); await this.reload(copy.id); showToast(t('mailing.toast.cloned'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async del(id: string) {
    if (!confirm(t('mailing.toast.confirmDeleteEmail'))) return;
    try { await api.del(`/mailing/emails/${id}`); this.newEmail(); await this.reload(); showToast(t('mailing.toast.emailDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private showError(r: Recipient) {
    this.errorDetail = { email: r.recipient_email, message: r.error_message || t('mailing.emails.noErrorDetailFailure') };
  }

  private renderErrorModal() {
    const d = this.errorDetail;
    return html`
      <cadence-modal ?open=${!!d} heading=${t('mailing.emails.sendError')} @close=${() => (this.errorDetail = null)}>
        ${d ? html`<p class="muted" style="margin-top:0">${t('mailing.emails.deliveryFailedPre')} <strong>${d.email}</strong> ${t('mailing.emails.deliveryFailedPost')}</p>
          <pre class="errtext">${d.message}</pre>` : ''}
        <div slot="footer"><cadence-button @click=${() => (this.errorDetail = null)}>${t('mailing.emails.close')}</cadence-button></div>
      </cadence-modal>`;
  }

  private renderEditor() {
    const listOptions = [{ value: '', label: t('mailing.compose.chooseList') }, ...this.lists.map((l) => ({ value: l.id, label: l.name }))];
    return html`
      <cadence-card heading=${this.selectedId ? t('mailing.emails.editDraft') : t('mailing.compose.newEmail')}>
        <div class="field">
          <cadence-input label=${t('mailing.compose.subject')} .value=${this.form.subject} @input=${(e: CustomEvent) => (this.form = { ...this.form, subject: e.detail })}></cadence-input>
        </div>
        <div class="field">
          <label>${t('mailing.compose.sendToList')}</label>
          <cadence-select .value=${this.form.mailing_list_id} .options=${listOptions}
            @change=${(e: CustomEvent) => (this.form = { ...this.form, mailing_list_id: e.detail })}></cadence-select>
          ${this.lists.length ? '' : html`<p class="muted">${t('mailing.compose.noListsYet')}</p>`}
        </div>
        <div class="field">
          <label>${t('mailing.compose.message')}</label>
          <cadence-rich-text-editor .value=${this.form.body_md} @input=${(e: CustomEvent) => (this.form = { ...this.form, body_md: e.detail })}></cadence-rich-text-editor>
        </div>
        <p class="muted">${t('mailing.compose.footerNote')}</p>
        <div class="actions" style="margin-top:12px">
          <cadence-button variant="secondary" ?loading=${this.saving} @click=${() => this.saveDraft()}>${t('mailing.compose.saveDraft')}</cadence-button>
          <cadence-button variant="ghost" @click=${() => (this.showPreview = true)}>${t('mailing.compose.preview')}</cadence-button>
          ${this.canSend ? html`<cadence-button variant="ghost" ?loading=${this.sendingTest} @click=${() => this.sendTest()}>${t('mailing.compose.sendTest')}</cadence-button>` : ''}
          ${this.canSend ? html`<cadence-button @click=${() => this.openSendDialog()}>${t('mailing.compose.sendEllipsis')}</cadence-button>` : ''}
          ${this.selectedId ? html`<button class="rm" @click=${() => this.del(this.selectedId!)}>${t('mailing.page.delete')}</button>` : ''}
        </div>
      </cadence-card>`;
  }

  /** A faithful preview of the final email as a recipient sees it: rendered body plus the
   *  auto-appended unsubscribe footer (the same renderMarkdown the server uses on send).
   *  TODO: replace the literal "your school" below with the actual school name. The *sent*
   *  email already uses it (the server passes schools.name into unsubscribeFooter); the
   *  preview can't yet because the client has no school-name source — add the school
   *  name/config to the bootstrap (/auth/session) or a /settings endpoint, then use it here
   *  and in the "From" line. */
  private renderPreviewModal() {
    return html`
      <cadence-modal ?open=${this.showPreview} heading=${t('mailing.compose.emailPreview')} @close=${() => (this.showPreview = false)}>
        <div class="mailframe">
          <div class="mf-subject">${this.form.subject || t('mailing.emails.noSubject')}</div>
          <div class="mf-from">${t('mailing.compose.fromLine')}</div>
          <div class="mf-body"><cadence-markdown-renderer .markdown=${this.form.body_md || t('mailing.compose.emptyMessage')}></cadence-markdown-renderer></div>
          <hr class="mf-hr" />
          <p class="mf-unsub">${t('mailing.compose.unsubLong')} <a @click=${(e: Event) => e.preventDefault()} href="#">${t('mailing.compose.unsubLink')}</a>.</p>
        </div>
        <div slot="footer"><cadence-button @click=${() => (this.showPreview = false)}>${t('mailing.emails.close')}</cadence-button></div>
      </cadence-modal>`;
  }

  /** The send-confirmation overlay: summarizes who the email goes to (list + counts) and
   *  shows an expandable preview, plus a "send test to me" shortcut before committing. */
  private renderSendModal() {
    const info = this.recipientInfo;
    const emailable = info?.subscribed ?? null;
    const listName = this.lists.find((l) => l.id === this.form.mailing_list_id)?.name ?? t('mailing.emails.listFallback');
    const myEmail = store.get().user?.email ?? t('mailing.compose.yourEmail');
    return html`
      <cadence-modal ?open=${this.showSendConfirm} heading=${t('mailing.compose.sendEmail')} @close=${() => (this.showSendConfirm = false)}>
        <dl class="summary">
          <div class="srow"><dt>${t('mailing.compose.subject')}</dt><dd>${this.form.subject || t('mailing.emails.noSubject')}</dd></div>
          <div class="srow"><dt>${t('mailing.compose.mailingList')}</dt><dd>${listName}</dd></div>
          <div class="srow"><dt>${t('mailing.emails.recipients')}</dt><dd>
            ${this.loadingRecipients
              ? html`<span class="muted">${t('mailing.compose.counting')}</span>`
              : info
                ? html`${emailable === 1 ? t('mailing.compose.emailable_one', { count: emailable }) : t('mailing.compose.emailable_other', { count: emailable ?? 0 })}${
                    info.total !== emailable ? html` <span class="muted">${t('mailing.compose.matchedUnsub', { total: info.total, count: info.total - (emailable ?? 0) })}</span>` : ''}`
                : html`<span class="muted">—</span>`}
          </dd></div>
        </dl>

        <div class="pv-head">
          <span>${t('mailing.compose.messagePreview')}</span>
          <button class="linkbtn" @click=${() => (this.previewExpanded = !this.previewExpanded)}>${this.previewExpanded ? t('mailing.compose.collapse') : t('mailing.compose.expand')}</button>
        </div>
        <div class="mailframe previewbox ${this.previewExpanded ? '' : 'collapsed'}">
          <div class="mf-subject">${this.form.subject || t('mailing.emails.noSubject')}</div>
          <div class="mf-from">${t('mailing.compose.fromLine')}</div>
          <div class="mf-body"><cadence-markdown-renderer .markdown=${this.form.body_md || t('mailing.compose.emptyMessage')}></cadence-markdown-renderer></div>
          <hr class="mf-hr" />
          <p class="mf-unsub">${t('mailing.compose.unsubShort')} <a @click=${(e: Event) => e.preventDefault()} href="#">${t('mailing.compose.unsubLink')}</a>.</p>
        </div>

        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.showSendConfirm = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button variant="secondary" title=${t('mailing.compose.sendTestTitle', { email: myEmail })} ?loading=${this.sendingTest} @click=${() => this.sendTest()}>${t('mailing.compose.sendTestToMe')}</cadence-button>
          <cadence-button ?loading=${this.sending} ?disabled=${this.loadingRecipients || emailable === 0}
            @click=${() => this.confirmSend()}>${emailable != null ? t('mailing.compose.sendToCount', { count: emailable }) : t('mailing.compose.send')}</cadence-button>
        </div>
      </cadence-modal>`;
  }

  private renderSent() {
    const e = this.detail!;
    return html`
      <cadence-card heading=${t('mailing.emails.sentEmail')}>
        <div class="actions" style="margin-bottom:12px">
          <strong style="font-size:1.05rem">${e.subject}</strong>
          <cadence-badge color="var(--color-success)">${t('mailing.status.sent')}</cadence-badge>
        </div>
        <p class="muted">${t('mailing.emails.to')} <strong>${e.mailing_list_name ?? t('mailing.emails.listFallback')}</strong> · ${e.recipient_count === 1 ? t('mailing.emails.recipientCount_one', { count: e.recipient_count }) : t('mailing.emails.recipientCount_other', { count: e.recipient_count })} · ${(e.sent_at ?? '').slice(0, 16).replace('T', ' ')}</p>
        <div class="sent-body"><cadence-markdown-renderer .markdown=${e.body_md}></cadence-markdown-renderer></div>
        <div class="actions" style="margin-top:12px">
          <cadence-button variant="ghost" @click=${() => (this.showPreview = true)}>${t('mailing.compose.preview')}</cadence-button>
          ${this.canManage ? html`
            <cadence-button variant="secondary" @click=${() => this.clone(e.id)}>${t('mailing.emails.cloneAsDraft')}</cadence-button>
            <button class="rm" @click=${() => this.del(e.id)}>${t('mailing.page.delete')}</button>` : ''}
        </div>
        ${(e.recipients ?? []).length ? html`
          <h4 style="margin:18px 0 6px">${t('mailing.emails.recipients')}</h4>
          <table class="rtable">
            <thead><tr><th>${t('mailing.columns.name')}</th><th>${t('mailing.columns.email')}</th><th>${t('mailing.columns.type')}</th><th>${t('mailing.columns.status')}</th></tr></thead>
            <tbody>
              ${(e.recipients ?? []).map((r) => html`<tr>
                <td>${r.recipient_name || '—'}</td><td>${r.recipient_email}</td>
                <td><span class="pill">${r.recipient_type}</span></td>
                <td><span class="pill ${r.status === 'failed' ? 'pill-fail' : ''}">${t('mailing.status.' + r.status)}</span>${
                  r.status === 'failed'
                    ? html`<button class="errbtn" title=${r.error_message || t('mailing.emails.noErrorDetailShort')} aria-label=${t('mailing.emails.showSendError')} @click=${() => this.showError(r)}>${icon('circle-alert', 15)}</button>`
                    : ''}</td>
              </tr>`)}
            </tbody>
          </table>` : ''}
      </cadence-card>`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    const isSent = this.detail?.status === 'sent';
    return html`
      <div style="padding-top:var(--space-4)" class="layout">
        <div>
          ${this.canManage ? html`<cadence-button full-width @click=${() => this.newEmail()}>${t('mailing.emails.newEmail')}</cadence-button>` : ''}
          <div class="list">
            ${this.emails.map((em) => html`
              <button class="eitem" aria-selected=${this.selectedId === em.id ? 'true' : 'false'} @click=${() => this.selectEmail(em.id)}>
                <div class="s">${em.subject || t('mailing.emails.noSubject')}
                  <cadence-badge color=${em.status === 'sent' ? 'var(--color-success)' : 'var(--color-text-secondary)'}>${t('mailing.status.' + em.status)}</cadence-badge></div>
                <div class="m">${em.status === 'sent' ? t('mailing.emails.sentCount', { count: em.recipient_count }) : (em.mailing_list_name ?? t('mailing.emails.noList'))}</div>
              </button>`)}
            ${this.emails.length ? '' : html`<p class="muted">${t('mailing.emails.noEmails')}</p>`}
          </div>
        </div>
        <div class="builder">
          ${isSent ? this.renderSent() : this.renderEditor()}
        </div>
      </div>
      ${this.renderPreviewModal()}
      ${this.renderSendModal()}
      ${this.renderErrorModal()}
    `;
  }
}
